import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, set } from 'idb-keyval';
import { atom, type Getter, useAtom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithStorage } from 'jotai/utils';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    createEditorValue,
    getEmptyEditorValue,
    isDocumentEmpty,
    trimDocument,
} from '@gitbook/doc-core';
import type { Value } from '@gitbook/slate';
import { useStateWithDeps } from '@gitbook/util-react-hooks';

import { getSerializedTableCellKey } from '../../state';
import { decodeDocumentFromAPI, encodeDocumentForAPI } from './encodeDocumentForAPI';

const BASIC_EDITOR_DRAFT_QUERY_KEY = ['basic-editor-draft'] as const;
const BASIC_EDITOR_DRAFT_STORAGE_PREFIX = '@gitbook/basic-editor-draft/';
const LEGACY_RECOIL_STORAGE_PREFIX = '@recoil/';
const LEGACY_JOTAI_STORAGE_PREFIX = '@jotai/';

// Only the document is persisted to localStorage; the value itself is held in memory, so it keeps
// its selection and focus state, and writing an unchanged value keeps its identity. Deriving the
// value from the stored JSON instead would mint a new one on every write, restarting any effect
// depending on the setter and never converging on a focused value.
const localStorageEditorValuesAtom = atomFamily((key: string) => {
    const documentAtom = atomWithStorage<api.JSONDocument | null>(
        `localStorageEditorValuesAtom__${JSON.stringify(key)}`,
        null,
        undefined,
        { getOnInit: true }
    );
    const memoryAtom = atom<Value | null>(null);

    const read = (get: Getter): Value | null => {
        const value = get(memoryAtom);
        if (value) {
            return value;
        }

        const document = get(documentAtom);
        return document ? createEditorValue(decodeDocumentFromAPI(document), 'basic') : null;
    };

    return atom(read, (get, set, input: React.SetStateAction<Value | null>) => {
        const nextValue = typeof input === 'function' ? input(read(get)) : input;
        set(memoryAtom, nextValue);
        set(documentAtom, nextValue ? encodeDocumentForAPI(nextValue.document) : null);
    });
});

/**
 * Utility hook to manage the state of a editor value coming from the API.
 */
export function useBasicEditorValue(initialInput?: api.JSONDocument) {
    const initialValue = React.useMemo(() => {
        return createEditorValue(initialInput ? decodeDocumentFromAPI(initialInput) : '', 'basic');
    }, [initialInput]);
    const [value, setValue] = useStateWithDeps(initialValue, [initialValue]);

    return useEditorValue(initialValue, value, setValue);
}

/**
 * Centralizes the storage key used for comment drafts so readers and writers cannot drift.
 * - mirrors the `getSerializedTableCellKey` pattern.
 * - includes the table cell identity so cells in the same block don't share a draft.
 * - falls back to the literal "undefined" rather than "" so page-level drafts
 *   stored, which use `undefined`, are not lost on upgrade.
 */
export function getCommentDraftKey(nodeKey?: string, tableCell?: api.TableCellTarget): string {
    return `comment:${getSerializedTableCellKey(tableCell) ?? nodeKey ?? 'undefined'}`;
}

/**
 * Returns true when there is a non-empty persisted draft for the given key.
 *
 * The indicator stays hidden until the asynchronous IndexedDB query has restored the draft.
 */
export function useHasDraftValue(key: string): boolean {
    const draftQuery = useBasicEditorDraftQuery(key);
    const value = draftQuery.data ?? null;
    return React.useMemo(() => {
        if (!value) {
            return false;
        }
        return !isDocumentEmpty(trimDocument(value.document));
    }, [value]);
}

/**
 * Like {@link useHasDraftValue}, but reads the localStorage-backed draft synchronously.
 */
export function useHasLocalStorageDraftValue(key: string): boolean {
    const value = useAtomValue(localStorageEditorValuesAtom(key));
    return React.useMemo(() => {
        if (!value) {
            return false;
        }
        return !isDocumentEmpty(trimDocument(value.document));
    }, [value]);
}

/**
 * Utility to manage the state of an editor value, persisted between reload, used for insertion.
 */
export function useBasicEditorPersistedValue(
    key: string,
    initialValue: Value = getEmptyEditorValue(key, 'basic'),
    options: {
        /**
         * Reset the value when the initial value is changed.
         * @default false
         */
        resetOnInitialValueChange?: boolean;
    } = {}
) {
    const { resetOnInitialValueChange = false } = options;
    const { data: persistedValue, isSuccess: isRestored } = useBasicEditorDraftQuery(key);
    const queryClient = useQueryClient();
    const queryKey = React.useMemo(() => getBasicEditorDraftQueryKey(key), [key]);
    const { mutate: saveDraft } = useMutation({
        mutationFn: persistBasicEditorDraft,
        scope: { id: `basic-editor-draft:${key}` },
        networkMode: 'always',
        onError: () => queryClient.invalidateQueries({ queryKey }),
    });
    const lastResetInitialValue = React.useRef<Value | undefined>(undefined);

    const setValue: React.Dispatch<React.SetStateAction<Value>> = React.useCallback(
        (input) => {
            const previous = queryClient.getQueryData<Value | null>(queryKey) ?? null;
            const nextValue = typeof input === 'function' ? input(previous || initialValue) : input;
            queryClient.setQueryData(queryKey, nextValue);
            saveDraft({ key, value: nextValue });
        },
        [initialValue, key, queryClient, queryKey, saveDraft]
    );

    React.useEffect(() => {
        if (
            !isRestored ||
            !resetOnInitialValueChange ||
            !initialValue ||
            lastResetInitialValue.current === initialValue
        ) {
            return;
        }

        lastResetInitialValue.current = initialValue;
        queryClient.setQueryData(queryKey, null);
        saveDraft({ key, value: null });
    }, [
        initialValue,
        isRestored,
        key,
        queryClient,
        queryKey,
        resetOnInitialValueChange,
        saveDraft,
    ]);

    return {
        ...useEditorValue(initialValue, persistedValue ?? initialValue, setValue),
        isRestored,
    };
}

/**
 * Like {@link useBasicEditorPersistedValue} but backed by localStorage for small drafts.
 */
export function useBasicEditorLocalStorageValue(
    key: string,
    initialValue: Value = getEmptyEditorValue(key, 'basic'),
    options: {
        /**
         * Reset the value when the initial value is changed.
         * @default false
         */
        resetOnInitialValueChange?: boolean;
    } = {}
) {
    const { resetOnInitialValueChange = false } = options;
    const [value, setNullableValue] = useAtom(localStorageEditorValuesAtom(key));

    // Written through the atom's updater so the setter identity does not depend on the current
    // value: callers use it as an effect dependency.
    const setValue: React.Dispatch<React.SetStateAction<Value>> = React.useCallback(
        (input) => {
            setNullableValue((prevValue) =>
                typeof input === 'function' ? input(prevValue || initialValue) : input
            );
        },
        [initialValue, setNullableValue]
    );

    React.useEffect(() => {
        if (resetOnInitialValueChange && initialValue) {
            setNullableValue(null);
        }
    }, [initialValue, resetOnInitialValueChange, setNullableValue]);

    return useEditorValue(initialValue, value || initialValue, setValue);
}

function getBasicEditorDraftQueryKey(key: string) {
    return [...BASIC_EDITOR_DRAFT_QUERY_KEY, key] as const;
}

function useBasicEditorDraftQuery(key: string) {
    const queryKey = React.useMemo(() => getBasicEditorDraftQueryKey(key), [key]);
    return useQuery({
        queryKey,
        queryFn: () => getBasicEditorDraft(key),
        gcTime: Infinity,
        meta: { persist: false },
        networkMode: 'always',
        staleTime: Infinity,
    });
}

async function getBasicEditorDraft(key: string): Promise<Value | null> {
    try {
        const storageKey = getBasicEditorDraftStorageKey(key);
        const legacyKey = `basicEditorValuesAtom__${JSON.stringify(key)}`;
        const storedDocument = await get<api.JSONDocument>(storageKey);
        const legacyDocument =
            (await get<api.JSONDocument>(`${LEGACY_JOTAI_STORAGE_PREFIX}${legacyKey}`)) ??
            (await get<api.JSONDocument>(`${LEGACY_RECOIL_STORAGE_PREFIX}${legacyKey}`));
        const document = storedDocument ?? legacyDocument;
        if (!storedDocument && legacyDocument) {
            await set(storageKey, legacyDocument);
        }
        return document ? createEditorValue(decodeDocumentFromAPI(document), 'basic') : null;
    } catch {
        return null;
    }
}

async function persistBasicEditorDraft(input: { key: string; value: Value | null }) {
    const storageKey = getBasicEditorDraftStorageKey(input.key);
    if (input.value) {
        await set(storageKey, encodeDocumentForAPI(input.value.document));
        return;
    }

    const legacyKey = `basicEditorValuesAtom__${JSON.stringify(input.key)}`;
    await Promise.all([
        del(storageKey),
        del(`${LEGACY_JOTAI_STORAGE_PREFIX}${legacyKey}`),
        del(`${LEGACY_RECOIL_STORAGE_PREFIX}${legacyKey}`),
    ]);
}

function getBasicEditorDraftStorageKey(key: string) {
    return `${BASIC_EDITOR_DRAFT_STORAGE_PREFIX}${key}`;
}

function useEditorValue(
    initialValue: Value,
    value: Value,
    setValue: React.Dispatch<React.SetStateAction<Value>>
) {
    const hasChanged = !value.document.equals(initialValue.document);

    const resetValue = React.useCallback(() => {
        setValue(createEditorValue('', 'basic'));
    }, [setValue]);

    const cancelChanges = React.useCallback(() => {
        setValue(initialValue);
    }, [setValue, initialValue]);

    return {
        value,
        initialValue,
        setValue,
        resetValue,
        cancelChanges,
        hasChanged,
    };
}
